import { useState, useMemo } from 'react';
import { Download } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import {
  QUICK_PERIODS, getQuickRange, BILLABLE_STATUSES,
  getDisplayStatus, exportCSV,
} from '../../utils/reportHelpers';
import { leadTimeDays } from '../trips/TripList';

const TRIP_TYPES = ['Self-Managed', 'STX-Managed', 'Group Event'];

const BANDS = [
  { key:'lastMinute',  label:'Last minute',  range:'0–3 days',   min:0,  max:3,   color:'#dc2626', bg:'#fef2f2', border:'#fecaca' },
  { key:'shortNotice', label:'Short notice', range:'4–10 days',  min:4,  max:10,  color:'#d97706', bg:'#fffbeb', border:'#fde68a' },
  { key:'standard',    label:'Standard',     range:'11–20 days', min:11, max:20,  color:'#ca8a04', bg:'#fefce8', border:'#fef08a' },
  { key:'wellPlanned', label:'Well planned', range:'21+ days',   min:21, max:Infinity, color:'#16a34a', bg:'#f0fdf4', border:'#bbf7d0' },
];

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function getBand(days) {
  if (days === null || days === undefined) return null;
  return BANDS.find(b => days >= b.min && days <= b.max) || null;
}

function monthLabel(yyyymm) {
  const [y, m] = yyyymm.split('-');
  return `${MONTHS_SHORT[parseInt(m,10)-1]} ${y}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d} ${MONTHS_SHORT[parseInt(m,10)-1]} ${y}`;
}

// Custom Recharts tooltip
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const val = payload[0]?.value;
  return (
    <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, padding:'8px 12px', fontSize:12, boxShadow:'0 2px 8px rgba(0,0,0,0.1)' }}>
      <div style={{ fontWeight:600, color:'#1e293b', marginBottom:2 }}>{label}</div>
      <div style={{ color:'#0d9488' }}>Avg lead time: <strong>{val !== null && val !== undefined ? `${val.toFixed(1)}d` : '—'}</strong></div>
    </div>
  );
}

export default function LeadTimeTrendReport({ trips }) {
  const now = new Date();
  const fy  = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  const [periodKey,  setPeriodKey]  = useState('thisFY');
  const [from,       setFrom]       = useState(`${fy}-07-01`);
  const [to,         setTo]         = useState(`${fy + 1}-06-30`);
  const [typeFilter, setTypeFilter] = useState([...TRIP_TYPES]);

  const applyPreset = (key) => {
    setPeriodKey(key);
    if (key === 'custom') return;
    const r = getQuickRange(key);
    setFrom(r.from);
    setTo(r.to);
  };

  const toggleType = (t) =>
    setTypeFilter(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

  // Only billable trips with a startDate in range
  const filtered = useMemo(() => {
    return trips.filter(t => {
      const ds = getDisplayStatus(t);
      if (!BILLABLE_STATUSES.has(ds)) return false;
      if (!t.startDate) return false;
      if (from && t.startDate < from) return false;
      if (to   && t.startDate > to)   return false;
      if (typeFilter.length && !typeFilter.includes(t.tripType)) return false;
      return true;
    });
  }, [trips, from, to, typeFilter]);

  // Enrich with lead time
  const enriched = useMemo(() =>
    filtered.map(t => ({ ...t, _leadDays: leadTimeDays(t) })).filter(t => t._leadDays !== null),
  [filtered]);

  // Present trip types (for showing type filter only when >1)
  const presentTypes = useMemo(() => {
    const s = new Set(trips.filter(t => BILLABLE_STATUSES.has(getDisplayStatus(t))).map(t => t.tripType).filter(Boolean));
    return TRIP_TYPES.filter(t => s.has(t));
  }, [trips]);

  // Summary stats
  const summary = useMemo(() => {
    if (!enriched.length) return null;
    const days = enriched.map(t => t._leadDays);
    const avg  = days.reduce((s, d) => s + d, 0) / days.length;
    const wellPlanned  = days.filter(d => d >= 21).length;
    const lastMinute   = days.filter(d => d <= 3).length;
    return {
      total:        enriched.length,
      avg,
      pctWell:      (wellPlanned / days.length) * 100,
      pctLastMin:   (lastMinute  / days.length) * 100,
    };
  }, [enriched]);

  // Monthly chart data — group by startDate YYYY-MM
  const chartData = useMemo(() => {
    const groups = {};
    enriched.forEach(t => {
      const key = t.startDate.slice(0, 7);
      if (!groups[key]) groups[key] = [];
      groups[key].push(t._leadDays);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([ym, daysList]) => ({
        month: monthLabel(ym),
        avg:   parseFloat((daysList.reduce((s, d) => s + d, 0) / daysList.length).toFixed(1)),
        count: daysList.length,
      }));
  }, [enriched]);

  // Band distribution
  const distribution = useMemo(() => {
    const total = enriched.length;
    return BANDS.map(b => {
      const count = enriched.filter(t => t._leadDays >= b.min && t._leadDays <= b.max).length;
      return { ...b, count, pct: total ? (count / total) * 100 : 0 };
    });
  }, [enriched]);

  // Short-notice trips (≤7 days), sorted ascending, max 20
  const shortNotice = useMemo(() =>
    enriched
      .filter(t => t._leadDays <= 7)
      .sort((a, b) => a._leadDays - b._leadDays)
      .slice(0, 20),
  [enriched]);

  const handleExport = () => {
    const headers = ['Trip', 'Traveller', 'Type', 'Start Date', 'Lead Time (days)', 'Band'];
    const rows = enriched.map(t => {
      const band = getBand(t._leadDays);
      return [
        t.title || t.tripRef || t.id,
        t.travellerName || '',
        t.tripType || '',
        t.startDate || '',
        t._leadDays,
        band ? band.label : '—',
      ];
    });
    exportCSV([headers, ...rows], `lead_time_trend_${from}_to_${to}.csv`);
  };

  return (
    <div style={{ fontFamily: "'DM Sans','Helvetica Neue',sans-serif" }}>

      {/* Filters */}
      <div style={card}>
        <div style={{ display:'flex', flexWrap:'wrap', gap:14, marginBottom: presentTypes.length > 1 ? 14 : 0 }}>
          <div>
            <label style={lbl}>Period</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
              {QUICK_PERIODS.map(p => (
                <button key={p.key} onClick={() => applyPreset(p.key)}
                  style={{ ...pill, ...(periodKey === p.key ? pillActive : pillInactive) }}>
                  {p.label}
                </button>
              ))}
              <button onClick={() => applyPreset('custom')}
                style={{ ...pill, ...(periodKey === 'custom' ? pillActive : pillInactive) }}>
                Custom
              </button>
            </div>
            <div style={{ display:'flex', gap:10, marginTop:8 }}>
              <div>
                <label style={lbl}>From</label>
                <input type="date" value={from}
                  onChange={e => { setFrom(e.target.value); setPeriodKey('custom'); }}
                  style={inp} />
              </div>
              <div>
                <label style={lbl}>To</label>
                <input type="date" value={to}
                  onChange={e => { setTo(e.target.value); setPeriodKey('custom'); }}
                  style={inp} />
              </div>
            </div>
          </div>
        </div>

        {presentTypes.length > 1 && (
          <div>
            <label style={lbl}>Trip Type</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
              {presentTypes.map(t => (
                <button key={t} onClick={() => toggleType(t)}
                  style={{ ...pill, ...(typeFilter.includes(t) ? pillActive : pillInactive) }}>
                  {typeFilter.includes(t) ? '✓ ' : ''}{t}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Empty state */}
      {enriched.length === 0 ? (
        <div style={{ ...card, textAlign:'center', padding:'60px 20px' }}>
          <div style={{ fontSize:13, color:'#475569' }}>No trips in this period.</div>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:12, marginBottom:20 }}>
            <div style={summaryCard}>
              <div style={summaryLabel}>Total Trips</div>
              <div style={summaryValue}>{summary.total}</div>
            </div>
            <div style={summaryCard}>
              <div style={summaryLabel}>Avg Lead Time</div>
              <div style={summaryValue}>{summary.avg.toFixed(1)}d</div>
            </div>
            <div style={summaryCard}>
              <div style={summaryLabel}>Well Planned (21+ days)</div>
              <div style={{ ...summaryValue, color:'#16a34a' }}>{summary.pctWell.toFixed(0)}%</div>
            </div>
            <div style={summaryCard}>
              <div style={summaryLabel}>Last Minute (0–3 days)</div>
              <div style={{ ...summaryValue, color:'#dc2626' }}>{summary.pctLastMin.toFixed(0)}%</div>
            </div>
          </div>

          {/* Monthly bar chart */}
          {chartData.length > 0 && (
            <div style={{ ...card, marginBottom:20 }}>
              <div style={sectionHeading}>Avg Lead Time by Month</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} margin={{ top:4, right:16, left:0, bottom:4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize:11, fill:'#475569' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize:11, fill:'#475569' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={v => `${v}d`}
                    width={36}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="avg" fill="#0d9488" radius={[4,4,0,0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Distribution */}
          <div style={{ ...card, marginBottom:20 }}>
            <div style={sectionHeading}>Lead Time Distribution</div>
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {distribution.map(b => (
                <div key={b.key}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ fontSize:12, fontWeight:600, padding:'2px 8px', borderRadius:20, background:b.bg, color:b.color, border:`1px solid ${b.border}` }}>
                        {b.label}
                      </span>
                      <span style={{ fontSize:12, color:'#475569' }}>{b.range}</span>
                    </div>
                    <div style={{ fontSize:12, color:'#374151', fontWeight:600 }}>
                      {b.count} trip{b.count !== 1 ? 's' : ''} · {b.pct.toFixed(0)}%
                    </div>
                  </div>
                  <div style={{ height:8, background:'#f1f5f9', borderRadius:4, overflow:'hidden' }}>
                    <div style={{
                      height:'100%',
                      width:`${b.pct}%`,
                      background: b.color,
                      borderRadius:4,
                      transition:'width 0.4s ease',
                      minWidth: b.count > 0 ? 4 : 0,
                    }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Short-notice trips table */}
          {shortNotice.length > 0 && (
            <div style={card}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                <div style={sectionHeading}>Short-Notice Trips (&le;7 days)</div>
                <button onClick={handleExport}
                  aria-label="Export lead time data as CSV"
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', background:'#0d9488', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                  <Download size={14} aria-hidden="true" />
                  Export CSV
                </button>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ background:'#f8fafc', borderBottom:'1px solid #e2e8f0' }}>
                      <th scope="col" style={th}>Trip</th>
                      <th scope="col" style={th}>Traveller</th>
                      <th scope="col" style={th}>Type</th>
                      <th scope="col" style={th}>Start Date</th>
                      <th scope="col" style={{ ...th, textAlign:'right' }}>Lead Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shortNotice.map((t, idx) => (
                      <tr key={t.id} style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa', borderBottom:'1px solid #f1f5f9' }}>
                        <td style={td}>
                          <div style={{ fontWeight:500, color:'#1e293b' }}>{t.title || t.tripRef || t.id}</div>
                        </td>
                        <td style={{ ...td, color:'#475569' }}>{t.travellerName || '—'}</td>
                        <td style={{ ...td, color:'#475569', whiteSpace:'nowrap' }}>{t.tripType || '—'}</td>
                        <td style={{ ...td, color:'#475569', whiteSpace:'nowrap' }}>{fmtDate(t.startDate)}</td>
                        <td style={{ ...td, textAlign:'right' }}>
                          <span style={{ fontSize:12, fontWeight:700, padding:'2px 8px', borderRadius:20, background:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca', whiteSpace:'nowrap' }}>
                            {t._leadDays}d
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {shortNotice.length === 20 && (
                <p style={{ fontSize:11, color:'#475569', marginTop:10, textAlign:'center' }}>
                  Showing top 20 shortest lead-time trips · Export CSV for full list
                </p>
              )}
            </div>
          )}

          {/* Export button when no short-notice trips */}
          {shortNotice.length === 0 && (
            <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
              <button onClick={handleExport}
                aria-label="Export lead time data as CSV"
                style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', background:'#0d9488', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                <Download size={14} aria-hidden="true" />
                Export CSV
              </button>
            </div>
          )}

          <p style={{ fontSize:11, color:'#475569', marginTop:4, textAlign:'center' }}>
            Filtered by trip start date · Lead time = days from trip creation to travel start date
            · Only billable trips (approved, booked, travelling, completed)
          </p>
        </>
      )}
    </div>
  );
}

// Style constants
const card         = { background:'#fff', borderRadius:12, border:'1px solid #e2e8f0', padding:'18px 20px', marginBottom:20, boxShadow:'0 1px 3px rgba(0,0,0,0.05)' };
const lbl          = { display:'block', fontSize:10, fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 };
const inp          = { padding:'7px 10px', border:'1px solid #cbd5e1', borderRadius:8, fontSize:13, color:'#1e293b', outline:'none', background:'#fff' };
const pill         = { padding:'5px 10px', border:'1px solid', borderRadius:6, fontSize:12, fontWeight:500, cursor:'pointer', transition:'all 0.15s' };
const pillActive   = { background:'#0d9488', color:'#fff', borderColor:'#0d9488' };
const pillInactive = { background:'#f1f5f9', color:'#475569', borderColor:'#e2e8f0' };
const summaryCard  = { background:'#fff', borderRadius:10, border:'1px solid #e2e8f0', padding:'14px 18px', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' };
const summaryLabel = { fontSize:10, fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 };
const summaryValue = { fontSize:22, fontWeight:800, color:'#0f172a', lineHeight:1.2 };
const sectionHeading = { fontSize:14, fontWeight:600, color:'#374151', marginBottom:12 };
const th = { padding:'10px 12px', textAlign:'left', fontSize:10, fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' };
const td = { padding:'10px 12px', color:'#374151', verticalAlign:'middle' };
